// Account upgrade — link the anonymous session to a real login, KEEPING THE
// SAME UID so all picks/streak/groups survive. This is the play-first spine
// (plan §4): "no anonymous accounts" stays true for iOS; web anon is a scoped
// exception whose whole purpose is to be upgraded in place.
//
// Providers: Apple, Google, Email (Facebook dropped — Tom 2026-06-13).
// Pure Firebase Auth — no new Cloud Function, no rules change. The +60 "secure
// your account" coin reward is WP3-C (server-side); this module just links.

import { firebase, auth, db } from '../firebase.js';

function currentUserOrThrow() {
  const u = auth.currentUser;
  if (!u) throw new Error('no-session');
  return u;
}

// Best-effort: if the linked provider gave us a real name and the user doc is
// still the "Guest" placeholder, adopt it. Uses update (not set) so it no-ops
// rather than creating a malformed doc when none exists yet. displayName is in
// the firestore.rules update whitelist, so this is permitted.
async function adoptProviderName() {
  const user = auth.currentUser;
  const name = user?.displayName?.trim();
  if (!name) return;
  try {
    await db
      .collection('users')
      .doc(user.uid)
      .update({ displayName: name.slice(0, 32) });
  } catch {
    /* doc may not exist yet, or name unchanged — non-critical */
  }
}

export async function linkGoogle() {
  const provider = new firebase.auth.GoogleAuthProvider();
  await currentUserOrThrow().linkWithPopup(provider);
  await adoptProviderName();
}

export async function linkApple() {
  const provider = new firebase.auth.OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  await currentUserOrThrow().linkWithPopup(provider);
  await adoptProviderName();
}

export async function linkEmail(email, password) {
  const cred = firebase.auth.EmailAuthProvider.credential(email, password);
  const result = await currentUserOrThrow().linkWithCredential(cred);
  // Non-blocking verification email (funnel audit R21): an email/password
  // account with a typo'd address can never receive a password reset — this is
  // the earliest, cheapest signal something's wrong. Apple/Google emails come
  // provider-verified, so only this path needs it. Never gates the save.
  try {
    await (result?.user || currentUserOrThrow()).sendEmailVerification();
  } catch (e) {
    console.warn('[authUpgrade] sendEmailVerification failed (non-blocking):', e);
  }
}

// ── Log IN to an EXISTING saved account ────────────────────────────────────
// Distinct from the link* functions above: those UPGRADE the current anonymous
// session in place (keep the same uid + progress). These SIGN IN to a
// previously-saved account — the anonymous session is replaced by the existing
// one. Used for account recovery (e.g. a returning user, a new device, or a
// session that was lost) — the user picks up their real account's picks/streak/
// coins. Any unsaved progress on the throwaway anon session is intentionally
// discarded (they're switching accounts, not merging).

export async function signInGoogle() {
  const provider = new firebase.auth.GoogleAuthProvider();
  await auth.signInWithPopup(provider);
}

export async function signInApple() {
  const provider = new firebase.auth.OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  await auth.signInWithPopup(provider);
}

export async function signInEmail(email, password) {
  await auth.signInWithEmailAndPassword(email, password);
}

// ── Forgot password ────────────────────────────────────────────────────────
// Self-serve recovery for an email/password account whose owner logged out and
// can't remember their password. iOS has this (EmailSignInSheet's "Forgot your
// password?"); web didn't, which stranded email savers on a new device. Pure
// Firebase Auth — sends the standard reset email. Firebase returns
// `auth/user-not-found` for an unknown email; the caller treats that as a no-op
// and shows the same confirmation, so we don't leak which emails have accounts.
export async function resetPassword(email) {
  await auth.sendPasswordResetEmail(email);
}

/**
 * Map a Firebase Auth error to user-facing copy. Returns null for benign
 * cancellations (popup closed / dismissed) — caller shows nothing.
 */
export function friendlyAuthError(e) {
  const code = e?.code || '';
  switch (code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
    case 'auth/user-cancelled':
      return null; // user backed out — not an error
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in popup — allow popups and try again.';
    case 'auth/operation-not-allowed':
      return 'That sign-in method isn’t switched on yet. Try another.';
    case 'auth/unauthorized-domain':
      // The Apple/Google OAuth popup is refused because this web origin isn't on
      // the Firebase Auth "Authorized domains" allowlist (Console → Authentication
      // → Settings). A config gap, NOT user-fixable — but email/password sign-in
      // is unaffected, so steer them to the path that works.
      return 'Apple & Google sign-in isn’t available on this page yet — use “Continue with Email” instead.';
    case 'auth/operation-not-supported-in-this-environment':
    case 'auth/web-storage-unsupported':
      // Some in-app / privacy-restricted browsers block the OAuth popup or its
      // storage. Email/password needs neither.
      return 'Your browser blocked Apple/Google sign-in — use “Continue with Email,” or open this page in Chrome or Safari.';
    case 'auth/credential-already-in-use':
    case 'auth/email-already-in-use':
    case 'auth/account-exists-with-different-credential':
      // Honest about the trade-off: logging in switches to the EXISTING account,
      // so any picks/coins on the current guest session won't carry over
      // (audit C2 — don't funnel users into silent progress loss).
      return 'You already have an account with this login. “Log in” switches to it — your current guest picks won’t carry over.';
    case 'auth/weak-password':
      return 'Password needs to be at least 6 characters.';
    case 'auth/invalid-email':
      return 'That doesn’t look like a valid email.';
    case 'auth/requires-recent-login':
      return 'Please refresh and try again.';
    // Log-in specific
    case 'auth/user-not-found':
      return 'No account found with that email. Try “Save your account” instead to create one.';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Email or password didn’t match. Try again.';
    case 'auth/too-many-requests':
      return 'Too many attempts — wait a moment and try again.';
    default:
      return 'Couldn’t complete that. Please try again.';
  }
}
